import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Send, Users, Briefcase } from "lucide-react";
import { toast } from "sonner";

export default function GuestRequestForm() {
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const canFlagWork = hasPerm("guest_requests.hospital_work");
  const [units, setUnits] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [sponsorOrgs, setSponsorOrgs] = useState([]);
  const [surveyOptions, setSurveyOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    guest_name: "", guest_phone: "", guest_email: "",
    relationship: "Family", purpose: "",
    arrival_date: "", departure_date: "", num_guests: 1,
    preferred_unit_id: "",
    sponsor_org_id: "",
    survey_ids: [],
    hospital_work: false,
    work_department: "",
    work_comments: "",
    transport: {
      needed: false, contact_phone: "", pickup_location: "", dropoff_location: "", notes: "",
      inbound: { date: "", time: "", flight_number: "", notes: "" },
      outbound: { date: "", time: "", flight_number: "", notes: "" },
    },
  });

  useEffect(() => {
    api.get("/housing/units").then(({ data }) => setUnits((data || []).filter((u) => u.active))).catch(() => {});
    api.get("/sponsor-orgs").then(({ data }) => setSponsorOrgs(data || [])).catch(() => setSponsorOrgs([]));
    api.get("/surveys/options").then(({ data }) => setSurveyOptions(data || [])).catch(() => {});
    api.get("/departments").then(({ data }) => setDepartments(data || [])).catch(() => {});
  }, []);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateTransport = (patch) => setForm((f) => ({ ...f, transport: { ...f.transport, ...patch } }));
  const updateLeg = (leg, patch) => setForm((f) => ({
    ...f,
    transport: { ...f.transport, [leg]: { ...f.transport[leg], ...patch } },
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.guest_name.trim()) return toast.error("Guest name is required");
    if (!form.arrival_date || !form.departure_date) return toast.error("Pick arrival + departure dates");
    if (form.departure_date <= form.arrival_date) return toast.error("Departure must be after arrival");
    if (form.hospital_work && !form.work_department) return toast.error("Select the department the guest will work with");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        num_guests: Math.max(1, parseInt(form.num_guests, 10) || 1),
        preferred_unit_id: form.preferred_unit_id || null,
        sponsor_org_id: form.sponsor_org_id || null,
        transport: form.transport.needed ? form.transport : null,
      };
      const { data } = await api.post("/guest-requests", payload);
      toast.success(
        form.hospital_work
          ? "Submitted — routed to the department head for approval"
          : data.conflict_check?.has_conflict
            ? "Submitted — note: dates clash with existing bookings/leases"
            : "Submitted — the coordinator will review shortly");
      navigate(`/guest-requests/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 fade-in" data-testid="guest-request-form-page">
      <Button variant="ghost" onClick={() => navigate("/guest-requests")} className="text-[#525C58]">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Guest Requests
      </Button>

      <div>
        <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
          New guest request
        </h1>
        <p className="text-[#525C58] mt-1">
          For non-medical visitors (family, friends, donors). The hospitality coordinator will review and reply.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <Card className="border-[#E8E8E6]">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-4 w-4 text-[#1B4332]" /> Guest details
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="gr-name">Guest name *</Label>
              <Input id="gr-name" data-testid="gr-name" value={form.guest_name}
                onChange={(e) => update({ guest_name: e.target.value })} required />
            </div>
            <div>
              <Label htmlFor="gr-phone">Guest phone</Label>
              <Input id="gr-phone" data-testid="gr-phone" value={form.guest_phone}
                onChange={(e) => update({ guest_phone: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="gr-email">Guest email</Label>
              <Input id="gr-email" type="email" data-testid="gr-email" value={form.guest_email}
                onChange={(e) => update({ guest_email: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="gr-rel">Relationship</Label>
              <Select value={form.relationship} onValueChange={(v) => update({ relationship: v })}>
                <SelectTrigger id="gr-rel" data-testid="gr-relationship"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Family", "Friend", "Donor", "Visitor", "Volunteer", "Other"].map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="gr-num">Number of guests</Label>
              <Input id="gr-num" type="number" min="1" data-testid="gr-num"
                value={form.num_guests} onChange={(e) => update({ num_guests: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="gr-purpose">Purpose of stay</Label>
              <Textarea id="gr-purpose" data-testid="gr-purpose" rows={2}
                placeholder="e.g. Visiting from home for the weekend"
                value={form.purpose} onChange={(e) => update({ purpose: e.target.value })} />
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E8E8E6]">
          <CardHeader>
            <CardTitle className="text-lg">Stay dates</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="gr-arrival">Arrival *</Label>
              <Input id="gr-arrival" type="date" data-testid="gr-arrival"
                value={form.arrival_date} onChange={(e) => update({ arrival_date: e.target.value })} required />
            </div>
            <div>
              <Label htmlFor="gr-departure">Departure *</Label>
              <Input id="gr-departure" type="date" data-testid="gr-departure"
                value={form.departure_date} onChange={(e) => update({ departure_date: e.target.value })} required />
            </div>
            <div>
              <Label htmlFor="gr-unit">Preferred unit (optional)</Label>
              <Select value={form.preferred_unit_id || "none"} onValueChange={(v) => update({ preferred_unit_id: v === "none" ? "" : v })}>
                <SelectTrigger id="gr-unit" data-testid="gr-unit"><SelectValue placeholder="No preference" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No preference</SelectItem>
                  {units.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} (cap {u.capacity})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {canFlagWork && (
          <Card className={`border-[#E8E8E6] ${form.hospital_work ? "ring-1 ring-[#1B4332]/30" : ""}`}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-[#1B4332]" /> Hospital work participation
              </CardTitle>
              <div className="flex items-center gap-2">
                <Label htmlFor="gr-work" className="text-sm">Yes</Label>
                <Switch id="gr-work" data-testid="gr-hospital-work"
                  checked={form.hospital_work}
                  onCheckedChange={(v) => update({ hospital_work: v })} />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-[#525C58]">
                Will this guest at any time be present or participating in work at the hospital?
                If yes, the request first goes to the department head, then the CMO, before the
                hospitality coordinator arranges housing.
              </p>
              {form.hospital_work && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="gr-work-dept">Department *</Label>
                    <Select value={form.work_department || ""} onValueChange={(v) => update({ work_department: v })}>
                      <SelectTrigger id="gr-work-dept" data-testid="gr-work-department">
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        {departments.map((d) => (
                          <SelectItem key={d.key} value={d.key}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="gr-work-comments">Comments / explanation</Label>
                    <Textarea id="gr-work-comments" data-testid="gr-work-comments" rows={2}
                      placeholder="e.g. Shadowing in theatre for two mornings; observing ward rounds"
                      value={form.work_comments} onChange={(e) => update({ work_comments: e.target.value })} />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="border-[#E8E8E6]">
          <CardHeader>
            <CardTitle className="text-lg">Sending organisation <span className="text-[#7A8A84] font-normal text-sm">(optional)</span></CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={form.sponsor_org_id || "none"} onValueChange={(v) => update({ sponsor_org_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="gr-sponsor-org"><SelectValue placeholder="No sponsor selected" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {sponsorOrgs.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card className="border-[#E8E8E6]">
          <CardHeader>
            <CardTitle className="text-lg">Post-trip survey <span className="text-[#7A8A84] font-normal text-sm">(optional — sent automatically after departure)</span></CardTitle>
          </CardHeader>
          <CardContent>
            {surveyOptions.length === 0 ? (
              <p className="text-xs text-[#7A8A84]">No surveys configured yet.</p>
            ) : (
              <div className="flex flex-col gap-1" data-testid="gr-survey-picker">
                {surveyOptions.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer px-1 rounded hover:bg-[#F4F4F1]">
                    <input type="checkbox" checked={form.survey_ids.includes(s.id)}
                           onChange={() => update({ survey_ids: form.survey_ids.includes(s.id) ? form.survey_ids.filter((x) => x !== s.id) : [...form.survey_ids, s.id] })}
                           data-testid={`gr-survey-${s.id}`} />
                    {s.name}
                  </label>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-[#E8E8E6]">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Airport transport</CardTitle>
            <div className="flex items-center gap-2">
              <Label htmlFor="gr-tr-needed" className="text-sm">Needed?</Label>
              <Switch id="gr-tr-needed" data-testid="gr-tr-needed"
                checked={form.transport.needed}
                onCheckedChange={(v) => updateTransport({ needed: v })} />
            </div>
          </CardHeader>
          {form.transport.needed && (
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Inbound flight #</Label>
                <Input data-testid="gr-tr-in-flight" value={form.transport.inbound.flight_number}
                  onChange={(e) => updateLeg("inbound", { flight_number: e.target.value })} />
              </div>
              <div>
                <Label>Inbound date / time</Label>
                <div className="flex gap-2">
                  <Input type="date" data-testid="gr-tr-in-date" value={form.transport.inbound.date}
                    onChange={(e) => updateLeg("inbound", { date: e.target.value })} />
                  <Input type="time" value={form.transport.inbound.time}
                    onChange={(e) => updateLeg("inbound", { time: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Outbound flight #</Label>
                <Input data-testid="gr-tr-out-flight" value={form.transport.outbound.flight_number}
                  onChange={(e) => updateLeg("outbound", { flight_number: e.target.value })} />
              </div>
              <div>
                <Label>Outbound date / time</Label>
                <div className="flex gap-2">
                  <Input type="date" data-testid="gr-tr-out-date" value={form.transport.outbound.date}
                    onChange={(e) => updateLeg("outbound", { date: e.target.value })} />
                  <Input type="time" value={form.transport.outbound.time}
                    onChange={(e) => updateLeg("outbound", { time: e.target.value })} />
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Transport notes</Label>
                <Textarea rows={2} value={form.transport.notes}
                  onChange={(e) => updateTransport({ notes: e.target.value })}
                  placeholder="Special instructions, pickup contact, etc." />
              </div>
            </CardContent>
          )}
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/guest-requests")}>Cancel</Button>
          <Button type="submit" disabled={submitting} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="gr-submit-btn">
            <Send className="h-4 w-4 mr-2" /> {submitting ? "Submitting…" : "Submit request"}
          </Button>
        </div>
      </form>
    </div>
  );
}
